import React, { useState, useEffect } from "react";
import { getEndpoints, getRoleEndpoints, ApiError } from "../api";

const RoleForm = ({
  addRole,
  updateRole,
  selectedRole,
  setActiveView,
  setApiError,
}) => {
  const isCreate = selectedRole == null;

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    department: "",
    level: "Mid",
  });

  const [endpoints, setEndpoints] = useState([]);
  const [selectedEndpointIds, setSelectedEndpointIds] = useState([]);
  const [endpointsLoading, setEndpointsLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  const departments = [
    "IT",
    "Management",
    "Marketing",
    "Sales",
    "Finance",
    "Human Resources",
    "Security",
    "Other",
  ];

  const levels = ["Senior", "Mid", "Junior"];

  useEffect(() => {
    if (selectedRole) {
      setFormData({
        name: selectedRole.name,
        description: selectedRole.description || "",
        department: selectedRole.department || "",
        level: selectedRole.level || "Mid",
      });
    } else {
      setFormData({
        name: "",
        description: "",
        department: "",
        level: "Mid",
      });
    }
  }, [selectedRole]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setEndpointsLoading(true);
      setApiError?.(null);
      try {
        const list = await getEndpoints();
        if (!cancelled) setEndpoints(list || []);

        if (selectedRole) {
          const ids = await getRoleEndpoints(selectedRole.id);
          if (!cancelled) setSelectedEndpointIds(ids || []);
        } else {
          if (!cancelled) setSelectedEndpointIds([]);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          setApiError?.("Session expired. Please log in again.");
          return;
        }
        setApiError?.(err.message || "Failed to load endpoints");
      } finally {
        if (!cancelled) setEndpointsLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [selectedRole?.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const toggleEndpoint = (id) => {
    setSelectedEndpointIds((prev) =>
      prev.includes(id) ? prev.filter((e) => e !== id) : [...prev, id]
    );
  };

  const selectAllEndpoints = () => {
    const assignable = endpoints.filter((ep) => !ep.isPublic).map((ep) => ep.id);
    setSelectedEndpointIds((prev) => [...new Set([...prev, ...assignable])]);
  };

  const deselectAllEndpoints = () => {
    setSelectedEndpointIds((prev) =>
      prev.filter((id) => {
        const ep = endpoints.find((e) => e.id === id);
        return ep?.isPublic;
      })
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    setApiError?.(null);
    try {
      if (isCreate) {
        await addRole(formData, selectedEndpointIds);
      } else {
        await updateRole(
          { ...selectedRole, ...formData },
          selectedEndpointIds
        );
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) return;
      setApiError?.(err.message || (isCreate ? "Failed to create role" : "Failed to update role"));
    } finally {
      setSubmitLoading(false);
    }
  };

  const isEndpointChecked = (id) => selectedEndpointIds.includes(id);

  return (
    <div className="form-container">
      <h2>
        <span>{isCreate ? "➕" : "✏️"}</span> {isCreate ? "Create role" : "Edit role"}
      </h2>

      <form onSubmit={handleSubmit}>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="name">Role name {isCreate && "*"}</label>
            <input
              type="text"
              id="name"
              name="name"
              className="form-input"
              value={formData.name}
              readOnly={!isCreate}
              disabled={!isCreate}
              onChange={handleChange}
              required
              style={
                isCreate
                  ? {}
                  : { backgroundColor: "#eee", cursor: "not-allowed" }
              }
              title={isCreate ? "" : "Role name is fixed for access control."}
            />
          </div>

          <div className="form-group">
            <label htmlFor="department">Department *</label>
            <select
              id="department"
              name="department"
              className="form-input"
              value={formData.department}
              onChange={handleChange}
              required
            >
              <option value="">Select department</option>
              {departments.map((dept) => (
                <option key={dept} value={dept}>
                  {dept}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="level">Level *</label>
            <select
              id="level"
              name="level"
              className="form-input"
              value={formData.level}
              onChange={handleChange}
              required
            >
              {levels.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </div>

          <div className="form-group" style={{ flex: 2 }}>
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              className="form-input"
              value={formData.description}
              onChange={handleChange}
              placeholder="Enter role description"
              rows="3"
              style={{ resize: "vertical" }}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group" style={{ width: "100%" }}>
            <div
              className="role-preview"
              style={{
                backgroundColor: "#f8f9fa",
                padding: "1.5rem",
                borderRadius: "8px",
                border: "1px solid #e9ecef",
                marginTop: "1rem",
              }}
            >
              <h4 style={{ marginBottom: "0.5rem", color: "#2c3e50" }}>
                Role preview
              </h4>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  flexWrap: "wrap",
                }}
              >
                <span
                  style={{
                    backgroundColor: "#3498db",
                    color: "white",
                    padding: "0.5rem 1rem",
                    borderRadius: "20px",
                    fontWeight: "bold",
                    fontSize: "1.1rem",
                  }}
                >
                  {formData.name || "Role name"}
                </span>
                {formData.department && (
                  <span
                    style={{
                      backgroundColor: getDepartmentColor(formData.department),
                      color: "white",
                      padding: "0.5rem 1rem",
                      borderRadius: "20px",
                    }}
                  >
                    Department: {formData.department}
                  </span>
                )}
                {formData.level && (
                  <span
                    style={{
                      backgroundColor: getLevelColor(formData.level),
                      color: "white",
                      padding: "0.5rem 1rem",
                      borderRadius: "20px",
                    }}
                  >
                    Level: {formData.level}
                  </span>
                )}
              </div>
              {formData.description && (
                <p style={{ marginTop: "1rem", color: "#7f8c8d" }}>
                  <strong>Description:</strong> {formData.description}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* API endpoint access */}
        <div
          className="form-row"
          style={{
            marginTop: "1.5rem",
            paddingTop: "1.5rem",
            borderTop: "1px solid #e9ecef",
          }}
        >
          <div className="form-group" style={{ width: "100%" }}>
            <h4 style={{ marginBottom: "0.5rem", color: "#2c3e50" }}>
              API endpoint access
            </h4>
            <p style={{ fontSize: "0.9rem", color: "#7f8c8d", marginBottom: "0.75rem" }}>
              Choose which API endpoints this role can access. Public endpoints are always allowed.
            </p>

            {endpointsLoading ? (
              <p style={{ color: "#7f8c8d" }}>Loading endpoints…</p>
            ) : endpoints.length === 0 ? (
              <p style={{ color: "#7f8c8d" }}>No endpoints defined.</p>
            ) : (
              <>
                <div style={{ marginBottom: "0.75rem", display: "flex", gap: "0.5rem" }}>
                  <button
                    type="button"
                    className="cancel-btn"
                    style={{ padding: "0.35rem 0.75rem", fontSize: "0.85rem" }}
                    onClick={selectAllEndpoints}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="cancel-btn"
                    style={{ padding: "0.35rem 0.75rem", fontSize: "0.85rem" }}
                    onClick={deselectAllEndpoints}
                  >
                    Deselect all
                  </button>
                </div>
                <div
                  style={{
                    maxHeight: "280px",
                    overflowY: "auto",
                    border: "1px solid #e9ecef",
                    borderRadius: "8px",
                    backgroundColor: "#fff",
                  }}
                >
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f8f9fa", position: "sticky", top: 0 }}>
                        <th style={{ padding: "0.5rem 0.75rem", textAlign: "left", width: "40px" }} />
                        <th style={{ padding: "0.5rem 0.75rem", textAlign: "left", minWidth: "140px" }}>Name</th>
                        <th style={{ padding: "0.5rem 0.75rem", textAlign: "left" }}>Code</th>
                        <th style={{ padding: "0.5rem 0.75rem", textAlign: "left", width: "70px" }}>Method</th>
                        <th style={{ padding: "0.5rem 0.75rem", textAlign: "left" }}>Path</th>
                        <th style={{ padding: "0.5rem 0.75rem", textAlign: "left", width: "80px" }}>Access</th>
                      </tr>
                    </thead>
                    <tbody>
                      {endpoints.map((ep) => (
                        <tr
                          key={ep.id}
                          style={{
                            borderBottom: "1px solid #eee",
                            backgroundColor: ep.isPublic ? "#fafafa" : undefined,
                          }}
                        >
                          <td style={{ padding: "0.5rem 0.75rem" }}>
                            {ep.isPublic ? (
                              <span
                                style={{
                                  color: "#95a5a6",
                                  fontSize: "0.85rem",
                                  cursor: "default",
                                }}
                                title="Public endpoint"
                              >
                                —
                              </span>
                            ) : (
                              <input
                                type="checkbox"
                                checked={isEndpointChecked(ep.id)}
                                onChange={() => toggleEndpoint(ep.id)}
                                aria-label={`Allow ${ep.code}`}
                              />
                            )}
                          </td>
                          <td style={{ padding: "0.5rem 0.75rem", fontSize: "0.9rem" }}>
                            {ep.name || "—"}
                          </td>
                          <td style={{ padding: "0.5rem 0.75rem", fontFamily: "monospace", fontSize: "0.9rem" }}>
                            {ep.code}
                          </td>
                          <td style={{ padding: "0.5rem 0.75rem" }}>
                            <span
                              style={{
                                padding: "0.2rem 0.4rem",
                                borderRadius: "4px",
                                fontSize: "0.8rem",
                                backgroundColor:
                                  ep.method === "GET"
                                    ? "#e3f2fd"
                                    : ep.method === "POST"
                                    ? "#e8f5e9"
                                    : ep.method === "PUT" || ep.method === "PATCH"
                                    ? "#fff3e0"
                                    : ep.method === "DELETE"
                                    ? "#ffebee"
                                    : "#f5f5f5",
                              }}
                            >
                              {ep.method}
                            </span>
                          </td>
                          <td style={{ padding: "0.5rem 0.75rem", fontSize: "0.85rem", color: "#555" }}>
                            {ep.pathPattern}
                          </td>
                          <td style={{ padding: "0.5rem 0.75rem" }}>
                            {ep.isPublic ? (
                              <span
                                style={{
                                  fontSize: "0.75rem",
                                  color: "#7f8c8d",
                                  backgroundColor: "#ecf0f1",
                                  padding: "0.2rem 0.5rem",
                                  borderRadius: "4px",
                                }}
                              >
                                Public
                              </span>
                            ) : isEndpointChecked(ep.id) ? (
                              <span style={{ fontSize: "0.85rem", color: "#27ae60" }}>Allowed</span>
                            ) : (
                              <span style={{ fontSize: "0.85rem", color: "#95a5a6" }}>—</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="form-buttons" style={{ marginTop: "1.5rem" }}>
          <button
            type="button"
            className="cancel-btn"
            onClick={() => setActiveView("roles")}
            disabled={submitLoading}
          >
            Cancel
          </button>
          <button type="submit" className="submit-btn" disabled={submitLoading}>
            {submitLoading ? "Saving…" : isCreate ? "Create role" : "Save changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

const getDepartmentColor = (department) => {
  const colors = {
    IT: "#3498db",
    Management: "#2ecc71",
    Marketing: "#9b59b6",
    Sales: "#e67e22",
    Finance: "#e74c3c",
    "Human Resources": "#1abc9c",
    Security: "#f39c12",
    Other: "#95a5a6",
  };
  return colors[department] || "#95a5a6";
};

const getLevelColor = (level) => {
  const colors = {
    Senior: "#e74c3c",
    Mid: "#f39c12",
    Junior: "#2ecc71",
  };
  return colors[level] || "#f39c12";
};

export default RoleForm;
